import React, { Component } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { model, service } from "../data";
import {
    getAgBytesErrorClassNames,
    handlePicklistChange,
    mapToPicklistValue,
    onTextChange,
    prepareSelectableOptions,
} from "~/admin/utils";
import { messages } from "../../i18n-messages";
import { adminData, ADD, GUID, NAME, DETAILS } from "~/admin/data";
// Components
import { Section, SelectInput, SubSection } from "~/core";
import { OrgLevelList, PROPS_STATE_ABBREVIATION } from "~/admin/agBytes/components/org-level-list";
// Style
import "../../add-edit-shared-responsive.css";
import {
    colorOptionRenderer,
    getBackgroundGradientStyle,
} from "~/action-panel/components/layer-module/utils";
import "./add-edit-panel.css";

const DEFINED_LEGEND_NAME = "defined legend";

export interface IAddEditPanelProps {
    addEditPanel: { mode: boolean };
    apiErrors: string[];
    apiErrorModel: any;
    classificationMethodName: any[];
    colorRamps: any[];
    companyList: any[];
    fetchCompanyList: () => void;
    fetchData: boolean;
    fetchDropdownData: () => void;
    fetchFilteredLocationList: (company: string) => void;
    fetchRecord: (guid: string) => void;
    fetchUnitData: () => void;
    filteredLocationList: any[];
    importExportHeaderTitle: () => void;
    intl: intlShape;
    liftRecordData: (record: any) => void;
    needs: (list: any[], callback: () => void) => void;
    numberOfClassesOptions: [];
    onRecordChange?: (record: any) => void;
    record: any; // CustomPropTypes.systemDefaults;
    recordGuid: string;
    setBreadcrumbs: (arrayOfCrumbs: Array<string>) => void;
    userRole: boolean;
}
export interface IAddEditPanelState {
    [model.PROPS_ORG_LEVEL_LIST]: any[];
    [model.PROPS_DISPLAY_TYPE_NAME]: any[];
    [model.PROPS_CLASSIFICATION_METHOD_NAME]: any[];
    [model.PROPS_SYSTEM_ATTRIBUTE_NAME]: any[];
    colorRampGuid: string;
    isRec: boolean;
    isSampling: boolean;
    numberOfClasses: string;
    selectedCompany: string;
    classificationMethodGuid: string;
    selectedCompanyList: any[];
}

export class AddEditPanel extends Component<IAddEditPanelProps, IAddEditPanelState> {
    private surfaceDefaults: any;
    constructor(props) {
        super(props);
        this.surfaceDefaults = {};
        this.state = {
            [model.PROPS_ORG_LEVEL_LIST]: [],
            [model.PROPS_DISPLAY_TYPE_NAME]: [],
            [model.PROPS_CLASSIFICATION_METHOD_NAME]: [],
            [model.PROPS_SYSTEM_ATTRIBUTE_NAME]: [],
            colorRampGuid: "",
            isRec: false,
            isSampling: false,
            numberOfClasses: "",
            selectedCompany: "",
            classificationMethodGuid: "",
            selectedCompanyList: null,
        };
    }

    componentDidMount() {
        this.props.setBreadcrumbs([""]);
        this.surfaceDefaults = service.getDefaultRecord();
        this.props.onRecordChange && this.props.onRecordChange(this.surfaceDefaults);
        const { needs } = this.props;
        needs([this.props.fetchCompanyList(), this.props.fetchDropdownData()], () => {});

        if (this.props.recordGuid) {
            needs([this.props.fetchRecord(this.props.recordGuid)], () => {});
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.fetchData && !prevProps.fetchData) {
            this.props.liftRecordData(this.surfaceDefaults);
        }

        const recordChanged =
            this.props.addEditPanel.mode !== ADD &&
            this.props.record &&
            this.props.record !== prevProps.record;

        if (recordChanged) {
            this.surfaceDefaults = {
                ...this.surfaceDefaults,
                ...this.props.record,
            };
            this.props.onRecordChange && this.props.onRecordChange(this.surfaceDefaults);

            const selectedSystemAttribute =
                (this.state[model.PROPS_SYSTEM_ATTRIBUTE_NAME] || []).find(
                    ({ value }) =>
                        value.guid === this.props.record[model.PROPS_SYSTEM_ATTRIBUTE_GUID]
                ) || null;

            const systemAttributeDetails = selectedSystemAttribute?.value?.details || {};

            this.setState({
                classificationMethodGuid: this.props.record.classificationMethodGuid || "",
                colorRampGuid: this.props.record.colorRampGuid || "",
                isRec: systemAttributeDetails.isRec || false,
                isSampling: systemAttributeDetails.isSampling || false,
                numberOfClasses: this.props.record.numberOfClasses || "",
                selectedCompany: this.props.record.companyGuid || "",
            });

            this._onCompanyChange(this.props.record.companyGuid);
        }

        if (this.props.companyList !== prevProps.companyList) {
            this.setState({
                companyList: (this.props.companyList || []).map(({ name, guid }) => ({
                    label: name,
                    value: guid,
                })),
            });
        }

        if (this.props.filteredLocationList !== prevProps.filteredLocationList) {
            this.setState({
                filteredLocationList: (this.props.filteredLocationList || []).map(
                    ({ name, guid }) => ({
                        label: name,
                        value: guid,
                    })
                ),
            });
        }

        const shouldInitializeDropdowns =
            recordChanged ||
            this.props[model.PROPS_CLASSIFICATION_METHOD_NAME] !==
                prevProps[model.PROPS_CLASSIFICATION_METHOD_NAME] ||
            this.props[model.PROPS_SYSTEM_ATTRIBUTE_NAME] !==
                prevProps[model.PROPS_SYSTEM_ATTRIBUTE_NAME] ||
            this.props[model.PROPS_DISPLAY_TYPE_NAME] !==
                prevProps[model.PROPS_DISPLAY_TYPE_NAME] ||
            this.props.filteredLocationList !== prevProps.filteredLocationList;

        if (shouldInitializeDropdowns) {
            this._initializeDropdowns(this.props);
        }
    }

    _handleClassificationMethodChange(classificationMethodGuid) {
        let { numberOfClasses } = this.state;
        if (this._isDefinedLegend(classificationMethodGuid)) {
            numberOfClasses = "";
        }
        this.setState(
            {
                classificationMethodGuid,
            },
            () => this._updateNumClasses(numberOfClasses)
        );
    }

    _handleSystemAttributeChange(systemAttribute) {
        const details = systemAttribute?.details || {};
        this.setState({
            isRec: details.isRec,
            isSampling: details.isSampling,
        });
    }

    _initializeDropdowns = (nextProps) => {
        this._initializeClassificationMethods(nextProps);
        this._initializeSystemAttributes(nextProps);
        this._initializeOrgLevel(nextProps);
        this._initializeDisplayTypes(nextProps);
    };

    _initializeSystemAttributes = (nextProps) => {
        if (
            this.surfaceDefaults[model.PROPS_SYSTEM_ATTRIBUTE_GUID] != null &&
            nextProps[model.PROPS_SYSTEM_ATTRIBUTE_NAME]
        ) {
            const systemAttributes = prepareSelectableOptions(
                nextProps[model.PROPS_SYSTEM_ATTRIBUTE_NAME],
                {
                    guid: GUID,
                    label: NAME,
                    details: DETAILS,
                    id: this.surfaceDefaults[model.PROPS_SYSTEM_ATTRIBUTE_GUID],
                }
            );
            const selectedSystemAttribute =
                systemAttributes.find(
                    ({ value }) =>
                        value.guid === this.surfaceDefaults[model.PROPS_SYSTEM_ATTRIBUTE_GUID]
                ) || null;

            this._handleSystemAttributeChange(selectedSystemAttribute?.value);
            this.setState({
                [model.PROPS_SYSTEM_ATTRIBUTE_NAME]: systemAttributes,
            });
        }
    };

    _initializeDisplayTypes = (nextProps) => {
        if (
            this.surfaceDefaults[model.PROPS_DISPLAY_TYPE_GUID] != null &&
            nextProps[model.PROPS_DISPLAY_TYPE_NAME]
        ) {
            this.setState({
                [model.PROPS_DISPLAY_TYPE_NAME]: prepareSelectableOptions(
                    nextProps[model.PROPS_DISPLAY_TYPE_NAME],
                    {
                        guid: GUID,
                        label: NAME,
                        id: this.surfaceDefaults[model.PROPS_DISPLAY_TYPE_GUID],
                        details: DETAILS,
                    }
                ),
            });
        }
    };

    _initializeClassificationMethods = (nextProps) => {
        if (
            this.surfaceDefaults[model.PROPS_CLASSIFICATION_METHOD_GUID] != null &&
            nextProps[model.PROPS_CLASSIFICATION_METHOD_NAME]
        ) {
            this.setState({
                [model.PROPS_CLASSIFICATION_METHOD_NAME]: prepareSelectableOptions(
                    nextProps[model.PROPS_CLASSIFICATION_METHOD_NAME],
                    {
                        guid: GUID,
                        label: NAME,
                        id: this.surfaceDefaults[model.PROPS_CLASSIFICATION_METHOD_GUID],
                    }
                ),
            });
        }
    };

    _initializeOrgLevel = (nextProps) => {
        if (
            this.surfaceDefaults[model.PROPS_ORG_LEVEL_LIST] != null &&
            nextProps.filteredLocationList
        ) {
            this.setState({
                [model.PROPS_ORG_LEVEL_LIST]: nextProps.filteredLocationList || [],
            });
        }
    };

    _isDefinedLegend(classificationMethodGuid) {
        const { classificationMethodName } = this.props;
        const selectedClassificationMethod =
            classificationMethodName &&
            classificationMethodName.find((cm) => cm.guid === classificationMethodGuid);
        return (
            selectedClassificationMethod &&
            selectedClassificationMethod.name.toLowerCase() === DEFINED_LEGEND_NAME
        );
    }

    _onCompanyChange = (value) => {
        if (this.state.selectedCompany !== value) {
            this._onTextChange(model.PROPS_ORG_LEVEL_LIST, [], () => {});
        }
        this.setState(
            {
                selectedCompany: value,
                [model.PROPS_ORG_LEVEL_LIST]: [],
            },
            () => {
                this.props.needs(
                    [this.props.fetchFilteredLocationList(this.state.selectedCompany)],
                    this._onTextChange(model.PROPS_COMPANY_GUID, value)
                );
            }
        );
    };

    _onPicklistChange = ({ type, guid }, value, callback) => {
        this.surfaceDefaults = handlePicklistChange(
            this.surfaceDefaults,
            { type, guid, value },
            callback
        );
        this.props.onRecordChange && this.props.onRecordChange(this.surfaceDefaults);
    };

    _onTextChange = (formKey, value, callback) => {
        this.surfaceDefaults = onTextChange(
            this.surfaceDefaults,
            { formKey: [formKey], value },
            callback
        );
        this.props.onRecordChange && this.props.onRecordChange(this.surfaceDefaults);
    };

    _updateColorRamp(colorRampGuid) {
        this.setState({ colorRampGuid });
        this._onTextChange(model.PROPS_COLOR_RAMP_GUID, colorRampGuid, () => {});
    }

    _updateNumClasses(numberOfClasses) {
        this.setState({ numberOfClasses });
        this._onTextChange(model.PROPS_NUMBER_OF_CLASSES, numberOfClasses, () => {});
    }

    _renderSurfaceDefaultsInfo = () => {
        const { formatMessage } = this.props.intl;
        const { surfaceDefaults } = this;
        const { apiErrors, colorRamps, numberOfClassesOptions } = this.props;
        const { colorRampGuid, isRec, isSampling, numberOfClasses, classificationMethodGuid } =
            this.state;

        const colorRampSelectOptions = colorRamps.map((cr) => ({
            background: getBackgroundGradientStyle(cr.layerFiles.slice(-1)[0]),
            label: " ",
            value: cr.colorRampGuid,
            titleValue: cr.colorRampName,
        }));

        const isDefinedLegend = this._isDefinedLegend(classificationMethodGuid);

        const numClassesOptions = numberOfClassesOptions.map((noc) => ({
            label: noc,
            value: noc,
        }));

        const displayTypeOptions =
            this.state[model.PROPS_DISPLAY_TYPE_NAME] &&
            this.state[model.PROPS_DISPLAY_TYPE_NAME].filter(
                (o) =>
                    o.value.details.isRec === isRec &&
                    (isSampling && !isRec ? o.label.toLowerCase().includes("polygon") : true)
            );

        const disableColorRamp = isDefinedLegend && !isSampling;
        numClassesOptions.shift(); // Remove first item in the list

        const colorRamp = this.props.colorRamps.find((cs) => cs.colorRampGuid === colorRampGuid);
        const backgroundImageStyle = colorRamp
            ? getBackgroundGradientStyle(colorRamp.layerFiles.slice(-1)[0])
            : null;
        const backgroundImageCss = {
            backgroundImage: `${backgroundImageStyle}`,
        };
        return (
            <div className="section-container">
                <Section testId="surface-defaults-details-section">
                    <SubSection testId="surface-defaults-color-ramp-and-classification-subsection">
                        <SelectInput
                            inputCSS={backgroundImageCss}
                            disabled={disableColorRamp}
                            filterable={false}
                            onChange={(e) => this._updateColorRamp(e)}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={colorRampSelectOptions}
                            optionRenderer={colorOptionRenderer}
                            placeholderText={formatMessage(messages.colorRampName)}
                            ref={(ref) => (this.colorRampSelect = ref)}
                            value={colorRampGuid}
                            clearable={false}
                            containerClassNames={[getAgBytesErrorClassNames(158, apiErrors)]}
                            required
                            testId="surface-defaults-color-ramp-select"
                        />
                        <SelectInput
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.state[model.PROPS_CLASSIFICATION_METHOD_NAME]}
                            autofocus
                            value={mapToPicklistValue({
                                options: this.state[model.PROPS_CLASSIFICATION_METHOD_NAME],
                                selectedGuid:
                                    surfaceDefaults[model.PROPS_CLASSIFICATION_METHOD_GUID],
                            })}
                            onChange={(value) => {
                                this._onPicklistChange(
                                    {
                                        type: model.PROPS_CLASSIFICATION_METHOD_NAME,
                                        guid: model.PROPS_CLASSIFICATION_METHOD_GUID,
                                    },
                                    value,
                                    () => this._handleClassificationMethodChange(value.guid)
                                );
                            }}
                            placeholderText={formatMessage(messages.classificationMethodName)}
                            containerClassNames={[getAgBytesErrorClassNames(906, apiErrors)]}
                            clearable={false}
                            required
                            testId="surface-defaults-classification-method-select"
                        />
                    </SubSection>
                    <SubSection testId="surface-defaults-system-attribute-and-classes-subsection">
                        <SelectInput
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={this.state[model.PROPS_SYSTEM_ATTRIBUTE_NAME]}
                            autofocus
                            value={mapToPicklistValue({
                                options: this.state[model.PROPS_SYSTEM_ATTRIBUTE_NAME],
                                selectedGuid: surfaceDefaults[model.PROPS_SYSTEM_ATTRIBUTE_GUID],
                            })}
                            disabled={this.props.addEditPanel.mode === "EDIT"}
                            onChange={(value) => {
                                this._onPicklistChange(
                                    {
                                        type: model.PROPS_SYSTEM_ATTRIBUTE_NAME,
                                        guid: model.PROPS_SYSTEM_ATTRIBUTE_GUID,
                                    },
                                    value,
                                    () => this._handleSystemAttributeChange(value)
                                );
                            }}
                            placeholderText={formatMessage(messages.systemAttributeName)}
                            containerClassNames={[getAgBytesErrorClassNames(225, apiErrors)]}
                            clearable={false}
                            required
                            testId="surface-defaults-system-attribute-select"
                        />
                        {!isDefinedLegend && (
                            <SelectInput
                                filterable={false}
                                onChange={(e) => this._updateNumClasses(e)}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                options={numClassesOptions}
                                placeholderText={formatMessage(messages.numberOfClasses)}
                                value={numberOfClasses}
                                clearable={false}
                                required={true}
                                containerClassNames={[getAgBytesErrorClassNames(383, apiErrors)]}
                                testId="surface-defaults-number-of-classes-select"
                            />
                        )}
                    </SubSection>
                    <SubSection testId="surface-defaults-display-type-subsection">
                        <SelectInput
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            options={displayTypeOptions}
                            autofocus
                            value={mapToPicklistValue({
                                options: this.state[model.PROPS_DISPLAY_TYPE_NAME],
                                selectedGuid: surfaceDefaults[model.PROPS_DISPLAY_TYPE_GUID],
                            })}
                            onChange={(value) => {
                                this._onPicklistChange(
                                    {
                                        type: model.PROPS_DISPLAY_TYPE_NAME,
                                        guid: model.PROPS_DISPLAY_TYPE_GUID,
                                    },
                                    value,
                                    null
                                );
                            }}
                            placeholderText={formatMessage(messages.displayTypeName)}
                            containerClassNames={[getAgBytesErrorClassNames(225, apiErrors)]}
                            clearable={false}
                            required
                            testId="surface-defaults-display-type-select"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    _renderDetailInfo2 = () => {
        const { surfaceDefaults } = this;
        const { apiErrors } = this.props;
        const { formatMessage } = this.props.intl;
        return (
            <Section
                required
                className="grid-section"
                headerText={formatMessage(messages.orgLevelList)}
                testId="surface-defaults-org-level-list-section"
            >
                {this.state.companyList && this.state.companyList.length === 1 ? null : (
                    <SubSection
                        className="select-company-container"
                        testId="surface-defaults-select-company-subsection"
                    >
                        <div className="select-company-message">
                            {formatMessage(messages.selectACompanyMessage)}
                        </div>
                        <SelectInput
                            required
                            clearable={false}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            placeholderText={formatMessage(messages.companyName)}
                            value={this.state.selectedCompany}
                            options={this.state.companyList}
                            onChange={(value) => {
                                this._onCompanyChange(value);
                            }}
                            containerClassNames={[getAgBytesErrorClassNames(909, apiErrors)]}
                            testId="surface-defaults-company-name-select"
                        />
                    </SubSection>
                )}
                {!this.state[model.PROPS_ORG_LEVEL_LIST] ? null : (
                    <OrgLevelList
                        apiErrors={apiErrors}
                        itemList={this.state[model.PROPS_ORG_LEVEL_LIST]}
                        record={surfaceDefaults[model.PROPS_ORG_LEVEL_LIST]}
                        onSelectionChange={(value) => {
                            this._onTextChange(model.PROPS_ORG_LEVEL_LIST, value, () =>
                                this.forceUpdate()
                            );
                        }}
                        statePropName={PROPS_STATE_ABBREVIATION}
                    />
                )}
            </Section>
        );
    };

    render() {
        return (
            <div className="add-edit-panel">
                <div className="section-container">
                    {this._renderSurfaceDefaultsInfo()}
                    <span className="bar section-fence" />
                    {this._renderDetailInfo2()}
                    <span className="bar section-fence" />
                </div>
            </div>
        );
    }
}

export default injectIntl(AddEditPanel);
